import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { renameThread, deleteThread } from "../../../../../api/chat";
import { chatRowClasses } from "./chatUtils";

const THREADS_PER_PAGE = 5;

const ChatThreadDirectory = ({
  dirKey,
  label,
  Icon,
  color,
  threads,
  currentThreadId,
  isExpanded,
  onToggle,
  onSelect,
  threadRefreshKey,
  onReload,
}) => {
  const [visibleCount, setVisibleCount] = useState(THREADS_PER_PAGE);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const renameInputRef = useRef(null);

  /* Reset visible count when threads refresh (e.g. new thread created) */
  useEffect(() => {
    setVisibleCount(THREADS_PER_PAGE);
  }, [threadRefreshKey]);

  /* Reset visible count when section is collapsed then reopened */
  useEffect(() => {
    if (isExpanded) setVisibleCount(THREADS_PER_PAGE);
  }, [isExpanded]);
  /* ── Listen for rename/delete triggers from right-click menu ── */
  useEffect(() => {
    const onRename = (e) => {
      const id = e.detail?.threadId;
      if (!id) return;
      const thread = threads.find((t) => t.id === id);
      if (!thread) return;
      setRenamingId(id);
      setRenameValue(thread.label);
      setDeletingId(null);
    };
    const onDelete = (e) => {
      const id = e.detail?.threadId;
      if (!id) return;
      if (!threads.find((t) => t.id === id)) return;
      setDeletingId(id);
      setRenamingId(null);
    };
    window.addEventListener("chat-thread:start-rename", onRename);
    window.addEventListener("chat-thread:start-delete", onDelete);
    return () => {
      window.removeEventListener("chat-thread:start-rename", onRename);
      window.removeEventListener("chat-thread:start-delete", onDelete);
    };
  }, [threads]);

  /* ── Auto-focus + select rename input ── */
  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  const commitRename = useCallback(async () => {
    if (!renamingId) return;
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenamingId(null);
      return;
    }
    try {
      await renameThread({ thread_id: renamingId, title: trimmed });
      if (onReload) onReload();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-threads"));
    } catch (e) {
      console.error("Rename failed:", e);
    }
    setRenamingId(null);
  }, [renamingId, renameValue, onReload]);

  const cancelRename = useCallback(() => {
    setRenamingId(null);
    setRenameValue("");
  }, []);

  const commitDelete = useCallback(async () => {
    if (!deletingId) return;
    try {
      await deleteThread({ thread_id: deletingId });
      if (onReload) onReload();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-threads"));
    } catch (e) {
      console.error("Delete failed:", e);
    }
    setDeletingId(null);
  }, [deletingId, onReload]);

  const cancelDelete = useCallback(() => {
    setDeletingId(null);
  }, []);
  const activeThread = currentThreadId
    ? threads.find((t) => t.id === currentThreadId)
    : null;

  const visibleThreads = threads.slice(0, visibleCount);
  const hasMore = visibleCount < threads.length;

  return (
    <div>
      <button
        onClick={() => onToggle(dirKey)}
        className="group w-full flex items-center gap-x-2 rounded-md px-2 py-1.5 text-[12px] text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition-colors"
      >
        <Icon className="h-3 w-3 shrink-0" strokeWidth={2} style={{ color }} />
        <span className="flex-1 text-left font-medium">{label}</span>
        {isExpanded ? (
          <ChevronUp
            className="h-3 w-3 shrink-0 text-zinc-400"
            strokeWidth={2}
          />
        ) : (
          <ChevronDown
            className="h-3 w-3 shrink-0 text-zinc-400"
            strokeWidth={2}
          />
        )}
      </button>

      {/* Collapsed: show active thread peek */}
      {!isExpanded && activeThread && (
        <div className="ml-2 border-l border-zinc-200/60 dark:border-zinc-700/40 pl-2">
          <button
            onClick={() => onSelect(activeThread.id, dirKey)}
            title={activeThread.label}
            className={chatRowClasses(true)}
          >
            <span className="truncate">{activeThread.label}</span>
          </button>
        </div>
      )}

      {/* Expanded: paginated thread list */}
      {isExpanded && (
        <div className="ml-2 border-l border-zinc-200/60 dark:border-zinc-700/40 pl-2">
          {threads.length === 0 ? (
            <p className="px-2 py-1 text-[11px] text-zinc-400 dark:text-zinc-600 italic">
              No threads yet
            </p>
          ) : (
            <>
              {visibleThreads.map((thread) => (
                <div
                  key={thread.id}
                  data-context-menu="chat-thread"
                  data-context-payload={JSON.stringify({
                    thread_id: thread.id,
                    title: thread.label,
                    thread_type: thread.thread_type,
                  })}
                >
                  {renamingId === thread.id ? (
                    /* ── Inline rename input ── */
                    <div className="px-2 py-1">
                      <input
                        ref={renameInputRef}
                        type="text"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            commitRename();
                          }
                          if (e.key === "Escape") {
                            e.preventDefault();
                            cancelRename();
                          }
                        }}
                        onBlur={commitRename}
                        className="w-full text-[12px] bg-white dark:bg-zinc-800 border border-[#0EA5E9]/40 rounded-md px-2 py-1 text-zinc-700 dark:text-zinc-200 outline-none focus:border-[#0EA5E9] transition-colors"
                      />
                    </div>
                  ) : deletingId === thread.id ? (
                    /* ── Inline delete confirmation ── */
                    <div className="flex items-center gap-x-1.5 px-2 py-1.5 text-[11px]">
                      <span className="text-red-500 dark:text-red-400 truncate flex-1">
                        Delete this thread?
                      </span>
                      <button
                        onClick={commitDelete}
                        className="px-2 py-0.5 rounded bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 transition-colors font-medium"
                      >
                        Yes
                      </button>
                      <button
                        onClick={cancelDelete}
                        className="px-2 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-500 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    /* ── Normal thread row ── */
                    <button
                      onClick={() => onSelect(thread.id, dirKey)}
                      title={thread.label}
                      className={chatRowClasses(thread.id === currentThreadId)}
                    >
                      <span className="truncate">{thread.label}</span>
                    </button>
                  )}
                </div>
              ))}
              {hasMore && (
                <button
                  onClick={() =>
                    setVisibleCount((prev) => prev + THREADS_PER_PAGE)
                  }
                  className="w-full px-2 py-1.5 font-bold text-[11px] text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition-colors text-left"
                >
                  Load more…
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatThreadDirectory;
